import { format, parseISO } from 'date-fns'
import useInvasiveServiceInfoStore from 'src/components/common/InvasiveServiceInfo/stores/useInvasiveServiceInfoStore'
import useContextStore from 'src/store/useContextStore'
import type { CommonContextStore } from 'src/types/context'
import sendYaGoal from 'src/utils/sendYaGoal'
import getDateFnsLocaleByIso from 'src/functions/getDateFnsLocaleByIso'
import { SERVICE_SLOT_GOAL } from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuPrices/constants'
import redirectToServiceAppointment from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuPrices/functions/redirectToServiceAppointment'
import type { SynonymPrice } from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuPrices/types'

type UseServiceAppointmentOptions = {
  lpuId: number
  townTranslit: string
  isEnabledDirect: boolean
}

type BookOptions = {
  byReferral?: boolean
  slot?: string
  date?: string
  shouldSendSlotGoals?: boolean
}

function getInvasiveState(service: SynonymPrice) {
  const allowTreatmentReferral = Boolean(service.allow_treatment_referral)
  const requiresPrimaryConsultation = Boolean(service.requires_primary_consultation)
  const hasPrimaryConsultationDoctors = Boolean(service.has_primary_consultation_doctors)

  return {
    allowTreatmentReferral,
    requiresPrimaryConsultation,
    hasPrimaryConsultationDoctors,
    doctorSpecialities: service.doctor_specialities || undefined,
    needsModal: allowTreatmentReferral
      || (requiresPrimaryConsultation && hasPrimaryConsultationDoctors),
  }
}

/**
 * Запись на услугу: инвазивная модалка и редирект на форму.
 *
 * Слоты грузит строка цены; сюда попадают пустой ответ и клик по времени.
 */
export default function useServiceAppointment({
  lpuId,
  townTranslit,
  isEnabledDirect,
}: UseServiceAppointmentOptions) {
  const invasiveServiceInfoStore = useInvasiveServiceInfoStore()
  const { context }: CommonContextStore = useContextStore()

  function formatSlotQuery(date: string, time: string) {
    const locale = getDateFnsLocaleByIso(context.current_language.iso)

    return `${format(parseISO(date), 'dd.MM.yyyy', { locale })} ${time}`
  }

  function redirect(service: SynonymPrice, options: BookOptions = {}) {
    redirectToServiceAppointment({
      lpuId,
      serviceId: service.synonym_id,
      byReferral: options.byReferral,
      slot: options.slot,
    })
  }

  function book(service: SynonymPrice, options: BookOptions = {}) {
    if (options.shouldSendSlotGoals && isEnabledDirect) {
      sendYaGoal(SERVICE_SLOT_GOAL.CLICK_LIST_DIRECT)
      sendYaGoal(SERVICE_SLOT_GOAL.CLICK_PRICE_LIST_DIRECT)
    }

    const invasive = getInvasiveState(service)

    if (!invasive.needsModal) {
      redirect(service, options)

      return
    }

    invasiveServiceInfoStore.openInfoModal({
      allowTreatmentReferral: invasive.allowTreatmentReferral,
      requiresPrimaryConsultation: invasive.requiresPrimaryConsultation,
      hasPrimaryConsultationDoctors: invasive.hasPrimaryConsultationDoctors,
      doctorSpecialities: invasive.doctorSpecialities,
      townTranslit,
      date: options.date,
      handleConfirmReferral: () => {
        sendYaGoal(SERVICE_SLOT_GOAL.CONFIRM_REFERRAL)
        redirect(service, {
          ...options,
          byReferral: true,
        })
      },
      handleAppointment: () => {
        sendYaGoal(SERVICE_SLOT_GOAL.CONFIRM_PRELIMINARY)
        redirect(service, options)
      },
    })
  }

  function onEmptySchedule(service: SynonymPrice) {
    book(service)
  }

  function onSlotClick(service: SynonymPrice, date: string, time: string) {
    book(service, {
      date,
      slot: formatSlotQuery(date, time),
      shouldSendSlotGoals: true,
    })
  }

  return {
    onEmptySchedule,
    onSlotClick,
  }
}
